import type { RequestOptions } from '@@/plugin-request/request';
import type { RequestConfig } from '@umijs/max';
import { message, notification } from 'antd';

// 错误处理方案： 错误类型
enum ErrorShowType {
  SILENT = 0,
  WARN_MESSAGE = 1,
  ERROR_MESSAGE = 2,
  NOTIFICATION = 3,
  REDIRECT = 9,
}
// 与后端约定的响应数据格式
interface ResponseStructure {
  success: boolean;
  data: any;
  errorCode?: number;
  errorMessage?: string;
  showType?: ErrorShowType;
}

// 状态码错误消息映射
const codeMessage: Record<number, string> = {
  401: '用户未授权，请重新登录',
  403: '禁止访问',
  404: '资源不存在',
  500: '服务器错误',
  // 其他状态码按需补充
};

const toLogin = () => {
  // Token过期，清除本地存储并跳转到登录页
  localStorage.removeItem('access_token');
  localStorage.removeItem('refresh_token');
  setTimeout(() => {
    // 保持当前端口号进行重定向
    const currentPort = window.location.port ? `:${window.location.port}` : '';
    window.location.href = `${window.location.protocol}//${window.location.hostname}${currentPort}/user/login`;
  }, 100);
};

/**
 * @name 错误处理
 * pro 自带的错误处理， 可以在这里做自己的改动
 * @doc https://umijs.org/docs/max/request#配置
 */
export const errorConfig: RequestConfig = {
  // 错误处理： umi@3 的错误处理方案。
  errorConfig: {
    // 错误抛出
    errorThrower: (res) => {
      const { success, data, errorCode, errorMessage, showType } = res as unknown as ResponseStructure;
      if (!success) {
        const error: any = new Error(errorMessage);
        error.name = 'BizError';
        error.info = { errorCode, errorMessage, showType, data };
        throw error; // 抛出自制的错误
      }
    },
    // 错误接收及处理
    errorHandler: (error: any, opts: any) => {
      if (opts?.skipErrorHandler) throw error;
      // 我们的 errorThrower 抛出的错误。
      if (error.name === 'BizError') {
        const errorInfo: ResponseStructure | undefined = error.info;
        if (errorInfo) {
          const { errorMessage, errorCode } = errorInfo;
          switch (errorInfo.showType) {
            case ErrorShowType.SILENT:
              // do nothing
              break;
            case ErrorShowType.WARN_MESSAGE:
              message.warning(errorMessage);
              break;
            case ErrorShowType.ERROR_MESSAGE:
              message.error(errorMessage);
              break;
            case ErrorShowType.NOTIFICATION:
              notification.open({
                description: errorMessage,
                message: errorCode,
              });
              break;
            case ErrorShowType.REDIRECT:
              // TODO: redirect
              break;
            default:
              message.error(errorMessage);
          }
        }
      } else if (error.response) {
        // Axios 的错误
        // 请求成功发出且服务器也响应了状态码，但状态代码超出了 2xx 的范围
        const { data, status } = error.response;

        // 优先显示后端返回的具体错误消息
        if (status == 401) {
          message.error('登录token过期，请重新登录');
          toLogin();
        } else if (data && data.message) {
          message.error(data.message);
        } else if (data && data.errorMessage) {
          message.error(data.errorMessage);
        } else {
          // 如果没有具体错误消息，才显示状态码
          message.error(`请求失败 (${error.response.status})`);
        }
      } else if (error.request) {
        // 请求已经成功发起，但没有收到响应
        // \`error.request\` 在浏览器中是 XMLHttpRequest 的实例，
        // 而在node.js中是 http.ClientRequest 的实例
        message.error('None response! Please retry.');
      } else {
        // 发送请求时出了点问题
        message.error('Request error, please retry.');
      }
    },
  },

  // 请求拦截器
  requestInterceptors: [
    (config: RequestOptions) => {
      // 拦截请求配置，进行个性化处理。
      // 如果需要添加认证token，应该从localStorage获取
      const token = localStorage.getItem('access_token');
      if (token) {
        config.headers = {
          ...config.headers,
          token: token,
        };
      } else {
        console.log('🔍 [请求拦截器] 未找到access_token');
      }
      return config;
    },
  ],

  // 响应拦截器
  responseInterceptors: [
    (response) => {
      // 拦截响应数据，进行个性化处理
      const { data } = response as unknown as ResponseStructure;
      if (data?.success === false) {
        if (data?.errorCode === 401) {
          // Token过期，清除本地存储并跳转到登录页
          localStorage.removeItem('access_token');
          localStorage.removeItem('refresh_token');
          setTimeout(() => {
            // 保持当前端口号进行重定向
            const currentPort = window.location.port ? `:${window.location.port}` : '';
            window.location.href = `${window.location.protocol}//${window.location.hostname}${currentPort}/user/login`;
          }, 100);
        }
        // 移除这里的通用错误消息，让具体的错误处理逻辑来处理
        // message.error('请求失败！');
      }
      return response;
    },
  ],
};
